import { useState } from 'react';
//import { useParams } from 'react-router-dom';
import { 
  Box, Paper, Typography, CircularProgress, List, ListItem, 
  ListItemText, ListItemIcon, ImageList, ImageListItem, 
  IconButton, Dialog, DialogContent 
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { Box as MuiBox } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useHomeAttachments, Attachment } from '../hooks/useHomeAttachments';

function HomeAttachments() {
  //const { homeId } = useParams<{ homeId: string }>();
  const [selectedImage, setSelectedImage] = useState<Attachment | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Use the enhanced hook
  const { 
    attachments, 
    isLoading, 
    error, 
    deleteAttachment, 
    getAttachmentUrl, 
    isImageFile 
  } = useHomeAttachments();

  // Split attachments by type
  const imageAttachments = attachments.filter(att => isImageFile(att.fileType));
  const otherAttachments = attachments.filter(att => !isImageFile(att.fileType));

  // Handle image deletion
  const handleDeleteAttachment = async (attachmentId: string) => {
    await deleteAttachment(attachmentId);
  };

  return (
    <Box sx={{ 
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      position: 'relative'
    }}>
      <Paper sx={{ 
        flex: 1, 
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        m: 3,
        p: 3 
      }}>
        <Typography variant="h6" gutterBottom sx={{ px: 2 }}>
          Attachments
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : attachments.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography color="text.secondary">No attachments found</Typography>
          </Box>
        ) : (
          <Box sx={{ overflow: 'auto' }}>
            {imageAttachments.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ px: 2, pb: 2 }}>
                  Images
                </Typography>
                <ImageList 
                  sx={{ width: '100%', px: 2 }} 
                  cols={isMobile ? 1 : isTablet ? 2 : 3}
                  gap={8}
                >
                  {imageAttachments.map((attachment) => (
                    <ImageListItem key={attachment.key} sx={{ position: 'relative' }}>
                      <MuiBox
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.8),
                          borderBottomLeftRadius: 8,
                          zIndex: 1
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAttachment(attachment.key);
                          }}
                          sx={{ 
                            p: 0.5,
                            '&:hover': {
                              color: 'error.main'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </MuiBox>
                      <img
                        src={getAttachmentUrl(attachment)}
                        alt={attachment.filename}
                        loading="lazy"
                        onClick={() => setSelectedImage(attachment)}
                        style={{ 
                          width: '100%',
                          height: '300px', // Increased height for better mobile viewing
                          objectFit: 'cover',
                          borderRadius: '4px',
                          cursor: 'pointer' // Add cursor pointer to indicate clickable
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              </>
            )}

            {otherAttachments.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ px: 2, py: 2 }}>
                  Other Files
                </Typography>
                <List>
                  {otherAttachments.map((attachment) => (
                    <ListItem key={attachment.key}>
                      <ListItemIcon>
                        <InsertDriveFileIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary={attachment.filename}
                        secondary={`Size: ${(attachment.size / 1024).toFixed(2)} KB`}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        )}
      </Paper>
      <Dialog
        open={selectedImage !== null}
        onClose={() => setSelectedImage(null)}
        maxWidth="xl"
        fullWidth
      >
        <DialogContent 
          sx={{ 
            p: 0, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            bgcolor: 'background.paper'
          }}
        >
          {selectedImage && (
            <img
              src={getAttachmentUrl(selectedImage)}
              alt={selectedImage.filename}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>
        <IconButton
          onClick={() => setSelectedImage(null)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            bgcolor: (theme) => alpha(theme.palette.common.black, 0.5),
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.7),
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Dialog>
    </Box>
  );
}

export default HomeAttachments;